import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  webFilterService,
  ReportSummary,
  PolicyStatusSummaryRow,
  PolicyStatusCategoryRow,
  TrafficDataRow,
  SecurityThreatSummary,
  SecurityThreatTop5Row,
  NonBusinessSiteRow,
} from '../services/webFilter.service';
import { pdfJobsService } from '../services/pdfJobs.service';
import seahLogo from '../assets/seah-logo.svg';
import WfReportSummary from '../components/web-filter/WfReportSummary';
import WfPolicyStatus1 from '../components/web-filter/WfPolicyStatus1';
import WfPolicyStatus2 from '../components/web-filter/WfPolicyStatus2';
import WfTrafficChart from '../components/web-filter/WfTrafficChart';
import WfSecurityThreatSummary from '../components/web-filter/WfSecurityThreatSummary';
import WfSecurityThreatTop5 from '../components/web-filter/WfSecurityThreatTop5';
import WfNonBusinessSites from '../components/web-filter/WfNonBusinessSites';
import { useApproval } from '../components/report/useApproval';
import './WebFilterPage.css';

const TABS = [
  { id: 'summary', label: '요약' },
  { id: 'policy1', label: '정책 현황 (1/2)' },
  { id: 'policy2', label: '정책 현황 (2/2)' },
  { id: 'traffic-web', label: '웹 트래픽' },
  { id: 'traffic-netapp', label: '넷앱 트래픽' },
  { id: 'threat-web-summary', label: '보안위협 웹 현황' },
  { id: 'threat-netapp-summary', label: '보안위협 넷앱 현황' },
  { id: 'threat-web-top5', label: '보안위협 웹 TOP5' },
  { id: 'threat-netapp-top5', label: '보안위협 넷앱 TOP5' },
  { id: 'non-business', label: '비업무사이트' },
];

const WebFilterPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [availableGroups, setAvailableGroups] = useState<string[]>([]);
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [isLoading, setIsLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isPdfMode, setIsPdfMode] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  // 데이터
  const [summary, setSummary] = useState<ReportSummary | null>(null);
  const [policy1, setPolicy1] = useState<{ rows: PolicyStatusSummaryRow[]; chartItems: any[] }>({ rows: [], chartItems: [] });
  const [policy2, setPolicy2] = useState<PolicyStatusCategoryRow[]>([]);
  const [trafficWeb, setTrafficWeb] = useState<TrafficDataRow[]>([]);
  const [trafficNetapp, setTrafficNetapp] = useState<TrafficDataRow[]>([]);
  const [threatWeb, setThreatWeb] = useState<SecurityThreatSummary>({ byAccount: [], byCategory: [] });
  const [threatNetapp, setThreatNetapp] = useState<SecurityThreatSummary>({ byAccount: [], byCategory: [] });
  const [top5Web, setTop5Web] = useState<SecurityThreatTop5Row[]>([]);
  const [top5Netapp, setTop5Netapp] = useState<SecurityThreatTop5Row[]>([]);
  const [nonBusiness, setNonBusiness] = useState<NonBusinessSiteRow[]>([]);

  const isAdminEditor = user?.role === 'admin' || user?.role === 'editor';

  // ────────────────────────────────────────────────────────────
  // 초기화 — URL 파라미터 처리
  // ────────────────────────────────────────────────────────────

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('pdf') === 'true') {
      setIsPdfMode(true);
      const p = urlParams.get('period');
      const g = urlParams.get('companyGroup');
      if (p) setSelectedPeriod(p);
      if (g) setSelectedGroup(g);
    }
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { approval, settled: approvalSettled } = useApproval(
    selectedPeriod && selectedGroup
      ? { pageType: 'WEB_FILTER', period: selectedPeriod, companyGroup: selectedGroup }
      : null,
  );

  // PDF 모드 window 신호 동기화 (결재란 포함) — __pdfDataLoaded 는 여기서만 세운다
  useEffect(() => {
    if (isPdfMode) {
      (window as any).__pdfDataLoaded = dataLoaded && approvalSettled;
      (window as any).__pdfIsLoading = isLoading;
    }
  }, [isPdfMode, dataLoaded, approvalSettled, isLoading]);

  // 그룹 변경 시 기간 목록 로드
  useEffect(() => {
    if (!selectedGroup) {
      setAvailablePeriods([]);
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    const isPdfModeFromUrl = urlParams.get('pdf') === 'true';
    webFilterService
      .getAvailablePeriods(selectedGroup)
      .then((periods) => {
        setAvailablePeriods(periods);
        if (!isPdfModeFromUrl && periods.length > 0 && !selectedPeriod) {
          setSelectedPeriod(periods[0]);
        }
      })
      .catch((err) => console.error('Failed to load periods:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  // 기간·그룹 모두 선택되면 데이터 로드
  useEffect(() => {
    if (selectedPeriod && selectedGroup) {
      setDataLoaded(false);
      loadReportData(selectedPeriod, selectedGroup);
    } else {
      setDataLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedGroup]);

  const loadInitialData = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const isPdfModeFromUrl = urlParams.get('pdf') === 'true';
    const periodParam = urlParams.get('period');
    const groupParam = urlParams.get('companyGroup');

    if (isPdfModeFromUrl && periodParam && groupParam) {
      setIsLoading(true);
      setDataLoaded(false);
      (window as any).__pdfDataLoaded = false;
      (window as any).__pdfIsLoading = true;
      try {
        const groups = await webFilterService.getPermittedCompanyGroups();
        setAvailableGroups(groups);
        setSelectedGroup(groupParam);
        setSelectedPeriod(periodParam);
        await loadReportData(periodParam, groupParam);
      } catch (e) {
        console.error('Failed to load PDF data:', e);
        setIsLoading(false);
        (window as any).__pdfDataLoaded = true;
        (window as any).__pdfIsLoading = false;
      }
      return;
    }

    setIsLoading(true);
    try {
      const groups = await webFilterService.getPermittedCompanyGroups();
      setAvailableGroups(groups);
      if (groups.length === 1) setSelectedGroup(groups[0]);
    } catch (e: any) {
      console.error('Failed to load initial data:', e);
      toast.error('초기 데이터 로드 실패: ' + (e?.message ?? 'unknown'));
    } finally {
      setIsLoading(false);
    }
  };

  const loadReportData = useCallback(async (period: string, group: string) => {
    const isPdfModeNow = new URLSearchParams(window.location.search).get('pdf') === 'true';

    setIsLoading(true);
    setDataLoaded(false);
    if (isPdfModeNow) {
      (window as any).__pdfIsLoading = true;
    }

    try {
      const [
        s,
        published,
        p1,
        p2,
        tWeb,
        tNetapp,
        thrWeb,
        thrNetapp,
        t5Web,
        t5Netapp,
        nb,
      ] = await Promise.all([
        webFilterService.getReportSummary(period, group).catch(() => null),
        webFilterService.getPublishStatus(period, group).catch(() => false),
        webFilterService.getPolicyStatusSummary(period, group).catch(() => ({ rows: [], chartItems: [] })),
        webFilterService.getPolicyStatusCategory(period, group).catch(() => []),
        webFilterService.getTraffic(period, group, 'web').catch(() => []),
        webFilterService.getTraffic(period, group, 'netapp').catch(() => []),
        webFilterService.getSecurityThreatSummary(period, group, 'web').catch(() => ({ byAccount: [], byCategory: [] })),
        webFilterService.getSecurityThreatSummary(period, group, 'netapp').catch(() => ({ byAccount: [], byCategory: [] })),
        webFilterService.getSecurityThreatTop5(period, group, 'web').catch(() => []),
        webFilterService.getSecurityThreatTop5(period, group, 'netapp').catch(() => []),
        webFilterService.getNonBusinessSites(period, group).catch(() => []),
      ]);

      setSummary(s);
      setIsPublished(published);
      setPolicy1(p1);
      setPolicy2(p2);
      setTrafficWeb(tWeb);
      setTrafficNetapp(tNetapp);
      setThreatWeb(thrWeb);
      setThreatNetapp(thrNetapp);
      setTop5Web(t5Web);
      setTop5Netapp(t5Netapp);
      setNonBusiness(nb);
      setDataLoaded(true);
    } catch (e) {
      console.error('Failed to load report data:', e);
      setDataLoaded(true);
    } finally {
      setIsLoading(false);
      if (isPdfModeNow) (window as any).__pdfIsLoading = false;
    }
  }, []);

  // ────────────────────────────────────────────────────────────
  // PDF 다운로드
  // ────────────────────────────────────────────────────────────

  const handleDownloadPdf = async () => {
    if (!selectedPeriod || !selectedGroup) {
      toast.error('기간과 계열사 그룹을 선택해주세요.');
      return;
    }
    const toastId = toast.loading('PDF 작업 시작 중...');
    try {
      const { jobId } = await pdfJobsService.createJob('web-filter', selectedPeriod, selectedGroup);
      toast.loading('PDF 생성 중... 0초 경과', { id: toastId });
      const blob = await pdfJobsService.pollUntilDone(jobId, (elapsed) => {
        toast.loading(`PDF 생성 중... ${elapsed}초 경과`, { id: toastId });
      });
      const year = selectedPeriod.slice(0, 4);
      const month = selectedPeriod.slice(4);
      const fileName = `[${selectedGroup}] ${year}년 ${month}월 유해차단 솔루션 운영보고서.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF 다운로드 완료', { id: toastId });
    } catch (e: any) {
      console.error('PDF download failed:', e);
      toast.error('PDF 생성 실패: ' + (e?.response?.data?.message || e.message), { id: toastId });
    }
  };

  // ────────────────────────────────────────────────────────────
  // 렌더링
  // ────────────────────────────────────────────────────────────

  return (
    <div className={`web-filter-page ${isPdfMode ? 'pdf-mode' : ''}`}>
      {!isPdfMode && (
        <div className="page-header">
          <h1>유해차단 운영보고서</h1>
          <div className="header-controls">
            <div className="filter-group">
              <label htmlFor="wf-group-select">계열사 그룹:</label>
              <select
                id="wf-group-select"
                value={selectedGroup}
                onChange={(e) => {
                  setSelectedGroup(e.target.value);
                  setSelectedPeriod('');
                }}
                disabled={isLoading}
              >
                <option value="">선택하세요</option>
                {availableGroups.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="wf-period-select">기간:</label>
              <select
                id="wf-period-select"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                disabled={!selectedGroup || isLoading}
              >
                <option value="">{selectedGroup ? '선택하세요' : '계열사 그룹을 먼저 선택하세요'}</option>
                {availablePeriods.map((p) => (
                  <option key={p} value={p}>
                    {p.slice(0, 4)}년 {p.slice(4)}월
                  </option>
                ))}
              </select>
            </div>
            <button
              className="download-button"
              onClick={handleDownloadPdf}
              disabled={!selectedPeriod || !selectedGroup || isLoading}
            >
              PDF 다운로드
            </button>
          </div>
        </div>
      )}

      {!isPdfMode && !isPublished && selectedPeriod && selectedGroup && (
        <div className="publish-warning">
          <p>⚠️ 이 보고서는 아직 발행되지 않았습니다.</p>
        </div>
      )}

      {isLoading ? (
        <div className="loading">로딩 중...</div>
      ) : !isPdfMode && !selectedGroup ? (
        <div className="empty-state"><p>계열사 그룹을 선택해주세요.</p></div>
      ) : !isPdfMode && !selectedPeriod ? (
        <div className="empty-state"><p>기간을 선택해주세요.</p></div>
      ) : !isPdfMode && !isPublished && user?.role === 'viewer' ? (
        <></>
      ) : (
        <>
          {!isPdfMode && (
            <div className="tab-container">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  className={`tab-button ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <div className="tab-content tab-panel">
            {isPdfMode && (
              <div className="pdf-page-break pdf-cover-page">
                <div className="cover-content">
                  <div className="cover-logo">
                    <img src={seahLogo} alt="Seah Logo" className="cover-logo-image" />
                  </div>
                  <h1 className="cover-title">유해차단 솔루션 운영보고서</h1>
                  <h2 className="cover-company">[{selectedGroup}]</h2>
                  <p className="cover-period">
                    {selectedPeriod ? `${selectedPeriod.slice(0, 4)}년 ${selectedPeriod.slice(4)}월` : ''}
                  </p>
                </div>
              </div>
            )}

            {(isPdfMode || activeTab === 'summary') && (
              <WfReportSummary data={summary} isPdfMode={isPdfMode} approval={approval} />
            )}
            {(isPdfMode || activeTab === 'policy1') && (
              <WfPolicyStatus1 rows={policy1.rows} chartItems={policy1.chartItems} isPdfMode={isPdfMode} />
            )}
            {(isPdfMode || activeTab === 'policy2') && (
              <WfPolicyStatus2 rows={policy2} isPdfMode={isPdfMode} />
            )}
            {(isPdfMode || activeTab === 'traffic-web') && (
              <WfTrafficChart
                rows={trafficWeb}
                trafficType="web"
                period={selectedPeriod}
                companyGroup={selectedGroup}
                isPdfMode={isPdfMode}
                canEdit={isAdminEditor}
                onNoteUpdate={(recordDate, accountType, note) =>
                  setTrafficWeb((prev) =>
                    prev.map((r) =>
                      r.recordDate === recordDate && r.accountType === accountType
                        ? { ...r, note }
                        : r,
                    ),
                  )
                }
              />
            )}
            {(isPdfMode || activeTab === 'traffic-netapp') && (
              <WfTrafficChart
                rows={trafficNetapp}
                trafficType="netapp"
                period={selectedPeriod}
                companyGroup={selectedGroup}
                isPdfMode={isPdfMode}
                canEdit={isAdminEditor}
                onNoteUpdate={(recordDate, accountType, note) =>
                  setTrafficNetapp((prev) =>
                    prev.map((r) =>
                      r.recordDate === recordDate && r.accountType === accountType
                        ? { ...r, note }
                        : r,
                    ),
                  )
                }
              />
            )}
            {(isPdfMode || activeTab === 'threat-web-summary') && (
              <WfSecurityThreatSummary data={threatWeb} trafficType="web" pageIndicator="(1/4)" isPdfMode={isPdfMode} />
            )}
            {(isPdfMode || activeTab === 'threat-netapp-summary') && (
              <WfSecurityThreatSummary data={threatNetapp} trafficType="netapp" pageIndicator="(2/4)" isPdfMode={isPdfMode} />
            )}
            {(isPdfMode || activeTab === 'threat-web-top5') && (
              <WfSecurityThreatTop5 rows={top5Web} trafficType="web" pageIndicator="(3/4)" isPdfMode={isPdfMode} />
            )}
            {(isPdfMode || activeTab === 'threat-netapp-top5') && (
              <WfSecurityThreatTop5 rows={top5Netapp} trafficType="netapp" pageIndicator="(4/4)" isPdfMode={isPdfMode} />
            )}
            {(isPdfMode || activeTab === 'non-business') && (
              <WfNonBusinessSites rows={nonBusiness} isPdfMode={isPdfMode} />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WebFilterPage;
